import React, { useEffect, useState, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps } from 'types';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature, calculateArea } from 'components/Map/helper';
import { removeAlertByGroup } from 'redux/alerts/actions';
import { useComponentMount } from 'utilities/hooks.util';
import Terraformer from 'terraformer-arcgis-parser';

export const MapView: React.FC<Props> = ({ baseMapTheme, enquiryShape }) => {
  const [mapReady, setMapReady] = useState<boolean>(false);
  const dispatch = useDispatch();
  const extraMapModules = [
    MapSettings.modules.webMercatorUtils,
    MapSettings.modules.featureLayer
  ];

  const onMapReady = () => {
    const { loadedView } = window;

    loadedView.ui.remove('zoom');
    loadedView.ui.remove('attribution');
    loadedView.navigation.mouseWheelZoomEnabled = false;
    loadedView.navigation.momentumEnabled = false;
    loadedView.navigation.browserTouchPanEnabled = false;
    loadedView.navigation.gamepad.enabled = false;

    loadedView.on('drag', (event: any) => {
      event.stopPropagation();
    });

    loadedView.on('key-down', (event: any) => {
      event.stopPropagation();
    });

    loadedView.on('double-click', (event: any) => {
      event.stopPropagation();
    });

    setMapReady(true);
  };

  const setEnquiryShapeToMap = useCallback(
    (shape: any) => {
      const { loadedMapModules, loadedMap } = window;
      if (loadedMapModules) {
        const { Graphic, WebMercatorUtils, GraphicsLayer } = loadedMapModules;
        const currentFeatureLayer = new GraphicsLayer({
          id: 'current-enquiry-layer'
        });
        const geometry: any = Terraformer.convert(shape);
        const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
          geometry
        );
        const graphic = new Graphic({
          geometry: {
            ...webMercatorGeometry,
            type: 'polygon'
          },
          symbol:
            baseMapTheme === 'dark'
              ? MapSettings.polygonDarkSymbol
              : MapSettings.polygonSymbol
        });
        currentFeatureLayer.add(graphic);
        loadedMap.add(currentFeatureLayer, 0);
        const area = calculateArea(graphic.geometry.extent);
        const bufferSize = Math.sqrt(area) * 0.2;
        zoomToFeature(graphic.geometry.extent, bufferSize);
        window.mapOriginalExtent = graphic.geometry.extent;
      }
    },
    [baseMapTheme]
  );

  useEffect(() => {
    mapReady && enquiryShape && setEnquiryShapeToMap(enquiryShape);
  }, [enquiryShape, mapReady, setEnquiryShapeToMap]);

  useComponentMount(() => {
    return () => {
      dispatch(removeAlertByGroup('map-alert'));
    };
  });

  return (
    <div className="map--view">
      <Map
        showCurrentLocationButton={false}
        onReady={onMapReady}
        extraModules={extraMapModules}
      />
    </div>
  );
};

const mapStateToProps = ({ app }: RootState) => ({
  baseMapTheme: app.baseMapTheme
});

const connector = connect(mapStateToProps);

type ownProps = {
  enquiryShape?: any;
};

type Props = CFCProps<typeof connector> & ownProps;

export default React.memo(connector(MapView));
